/**
 * @Author: AI Assistant
 * @Date: 2026-01-13
 * @Description: API相关状态管理 - 管理余额、商品列表等数据
 */
import { defineStore } from 'pinia'
import { getApiBalanceApi, getApiPricingApi, getModelRatesApi } from '~/api/api'
import type { PackageItem } from '~/api/api'

/**
 * 模型费率数据结构
 */
export interface ModelRate {
    /** 模型名称 */
    name: string
    /** 消耗的积分 */
    consumption: string
}

/**
 * API状态接口定义
 */
interface ApiState {
    /** API余额 */
    balance: number
    /** 商品套餐列表 */
    packages: PackageItem[]
    /** 模型费率列表 */
    modelRates: ModelRate[]
    /** 余额是否已加载 */
    balanceLoaded: boolean
    /** 商品列表是否已加载 */
    packagesLoaded: boolean
    /** 模型费率是否已加载 */
    modelRatesLoaded: boolean
}

/**
 * API状态管理 Store
 */
export const useApiStore = defineStore('api', {
    /**
     * 初始状态定义
     */
    state: (): ApiState => ({
        balance: 0,
        packages: [],
        modelRates: [],
        balanceLoaded: false,
        packagesLoaded: false,
        modelRatesLoaded: false,
    }),

    /**
     * 计算属性
     */
    getters: {
        /**
         * 获取格式化后的余额（千分位）
         */
        formattedBalance: (state: ApiState): string => {
            return state.balance.toLocaleString('en-US')
        },

        /**
         * 获取商品列表
         */
        packageList: (state: ApiState): PackageItem[] => state.packages,

        /**
         * 获取模型费率列表
         */
        modelRateList: (state: ApiState): ModelRate[] => state.modelRates,

        /**
         * 是否有缓存数据
         */
        hasCache: (state: ApiState): boolean =>
            state.balanceLoaded && state.packagesLoaded && state.modelRatesLoaded,
    },

    /**
     * 状态操作方法
     */
    actions: {
        /**
         * 获取API余额
         * @param forceRefresh 是否强制刷新（忽略缓存）
         */
        async fetchBalance(forceRefresh: boolean = false) {
            try {
                const res = await getApiBalanceApi()
                if (res.status === 200) {
                    // data 直接就是余额数字
                    this.balance = res.data || 0
                    this.balanceLoaded = true
                }
                return this.balance
            } catch (error) {
                console.error('获取API余额失败:', error)
                return this.balance
            }
        },

        /**
         * 获取商品套餐列表
         * @param forceRefresh 是否强制刷新（忽略缓存）
         */
        async fetchPackages(forceRefresh: boolean = false) {
            // 如果已经加载过且不强制刷新，直接返回
            if (this.packagesLoaded && !forceRefresh) {
                return this.packages
            }

            try {
                const res = await getApiPricingApi()
                if (
                    res.status === 200 &&
                    res.data?.credits_shelf?.goods &&
                    Array.isArray(res.data.credits_shelf.goods)
                ) {
                    const goods = res.data.credits_shelf.goods

                    // 转换商品数据格式
                    this.packages = goods.map((item: any, index: number): PackageItem => {
                        // 提取积分数量（从name中提取数字，如 "850 Credits" -> 850）
                        const creditsMatch = item.name.match(
                            /(\d+(?:,\d+)?)\s*Credits?/i
                        )
                        const creditsNumber =
                            creditsMatch && creditsMatch[1]
                                ? parseInt(
                                    creditsMatch[1].replace(/,/g, ''),
                                    10
                                )
                                : 0

                        // 计算单价（price是分，需要转换为元）
                        const priceInYuan = item.price / 100
                        const unitPrice =
                            creditsNumber > 0
                                ? (priceInYuan / creditsNumber).toFixed(2)
                                : '0.00'

                        return {
                            id: item.id,
                            originalData: item, // 保留原始数据，用于下单
                            price: item.price_show, // 使用 price_show 显示价格
                            credits: creditsNumber.toLocaleString('en-US'), // 格式化积分数字
                            unitPrice: `$${unitPrice}`, // 单价
                        }
                    })

                    this.packagesLoaded = true
                }
                return this.packages
            } catch (error) {
                console.error('获取商品套餐列表失败:', error)
                return this.packages
            }
        },

        /**
         * 获取模型费率列表
         * @param forceRefresh 是否强制刷新（忽略缓存）
         */
        async fetchModelRates(forceRefresh: boolean = false) {
            // 如果已经加载过且不强制刷新，直接返回
            if (this.modelRatesLoaded && !forceRefresh) {
                return this.modelRates
            }

            try {
                const res = await getModelRatesApi()
                if (res.status === 200 && res.data && Array.isArray(res.data)) {
                    // 将后端返回的数据格式转换为前端需要的格式
                    this.modelRates = res.data.map((item: any): ModelRate => ({
                        name: item.model_name || '',
                        consumption: item.original_credits || '0',
                    }))
                    this.modelRatesLoaded = true
                }
                return this.modelRates
            } catch (error) {
                console.error('获取模型费率列表失败:', error)
                return this.modelRates
            }
        },

        /**
         * 同时加载余额、商品列表和模型费率
         * @param forceRefresh 是否强制刷新
         */
        async fetchAll(forceRefresh: boolean = false): Promise<void> {
            const store = this as any
            await Promise.all([
                store.fetchPackages(forceRefresh),
                store.fetchBalance(forceRefresh),
                store.fetchModelRates(forceRefresh),
            ])
        },

        /**
         * 刷新余额（强制重新获取）
         */
        async refreshBalance(): Promise<number> {
            const store = this as any
            return await store.fetchBalance(true)
        },

        /**
         * 刷新商品列表（强制重新获取）
         */
        async refreshPackages(): Promise<PackageItem[]> {
            const store = this as any
            return await store.fetchPackages(true)
        },

        /**
         * 刷新模型费率列表（强制重新获取）
         */
        async refreshModelRates(): Promise<ModelRate[]> {
            const store = this as any
            return await store.fetchModelRates(true)
        },

        /**
         * 清除缓存
         */
        clearCache() {
            this.balance = 0
            this.packages = []
            this.modelRates = []
            this.balanceLoaded = false
            this.packagesLoaded = false
            this.modelRatesLoaded = false
        },
    },
})
